import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Save, Trash2, Zap, Link2 } from "lucide-react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";

const FlowCard = ({ kind, flow, roles, onSaved }) => {
  const [mode, setMode] = useState(flow.mode || "instant");
  const [steps, setSteps] = useState(flow.steps || []);
  const [add, setAdd] = useState("");
  const [busy, setBusy] = useState(false);
  const move = (i, d) => { const s = [...steps]; [s[i], s[i + d]] = [s[i + d], s[i]]; setSteps(s); };
  const save = async () => {
    setBusy(true);
    try { await api.setApprovalFlow(kind, { mode, steps }); toast.success(`تم حفظ موافقات ${flow.label}`); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const tab = (v, icon, label) => (
    <button type="button" onClick={() => setMode(v)} data-testid={`flow-${kind}-mode-${v}`}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${mode === v ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{icon}{label}</button>
  );
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`flow-card-${kind}`}>
      <p className="font-heading font-bold text-slate-900">طلبات {flow.label}</p>
      {kind === "resignation" && <p className="text-[11px] text-slate-500">بعد آخر موافقة، الموارد البشرية تصدر قرار قبول الاستقالة.</p>}
      <div className="flex gap-2">{tab("instant", <Zap className="h-4 w-4" />, "موافقة فورية")}{tab("chain", <Link2 className="h-4 w-4" />, "سلسلة موافقات")}</div>
      {mode === "instant" ? <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">المدير / المالك فقط يوافق، والطلب يتنفّذ على طول.</p> : <>
        <ol className="space-y-1.5">{steps.map((s, i) => (
          <li key={`${s}-${i}`} className="flex items-center gap-2 rounded-xl bg-indigo-50/60 px-3 py-2 text-sm" data-testid={`flow-${kind}-step-${i}`}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">{i + 1}</span>
            <span className="flex-1 font-semibold text-slate-800">{roles[s]}</span>
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-30" data-testid={`flow-${kind}-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" disabled={i === steps.length - 1} onClick={() => move(i, 1)} className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-30" data-testid={`flow-${kind}-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
            <button type="button" onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="rounded p-1 text-rose-500 hover:bg-white" data-testid={`flow-${kind}-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </li>))}</ol>
        <div className="flex gap-2">
          <select value={add} onChange={(e) => setAdd(e.target.value)} className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`flow-${kind}-add-select`}>
            <option value="">اختر خطوة…</option>{Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Button variant="outline" disabled={!add} onClick={() => { setSteps([...steps, add]); setAdd(""); }} className="gap-1" data-testid={`flow-${kind}-add-btn`}><Plus className="h-4 w-4" /> إضافة خطوة</Button>
        </div>
        <p className="text-[11px] text-slate-500">الطلب يمشي بالترتيب، وكل خطوة توافق قبل اللي بعدها. أي خطوة ما لها شخص في الشركة تنتخطى تلقائياً، والمالك يقدر يوافق في أي خطوة.</p>
      </>}
      <Button onClick={save} disabled={busy} className="gap-1.5" data-testid={`flow-${kind}-save`}><Save className="h-4 w-4" /> حفظ</Button>
    </div>
  );
};

export const ApprovalFlowsSettings = () => {
  const [data, setData] = useState(null);
  const load = () => api.approvalFlows().then(setData).catch(() => toast.error("تعذّر تحميل الموافقات"));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="approval-flows-settings">
      <p className="text-sm text-slate-600">حدّد لكل نوع طلب من المناديب: موافقة فورية من المدير، أو سلسلة موافقات (مثلاً المشرف ← الموارد البشرية ← المحاسب ← المدير).</p>
      {Object.entries(data.kinds).map(([k, f]) => <FlowCard key={k} kind={k} flow={f} roles={data.roles} onSaved={load} />)}
    </div>
  );
};
